import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const Login: React.FC = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const navigate = useNavigate()

  // Get branding settings from localStorage
  const [branding, setBranding] = useState({
    systemName: 'PIS',
    systemFullName: 'Pathology Information System',
    systemIcon: '🏥',
    logoUrl: '',
    loginPageTitle: 'Welcome to PIS',
    loginPageSubtitle: 'Advanced digital pathology platform',
    loginPageDescription: 'Advanced digital pathology platform for medical imaging, diagnosis management, and laboratory workflow optimization.',
    loginFeature1: '🔬 Digital Pathology Management',
    loginFeature2: '📊 Advanced Analytics & Reports',
    loginFeature3: '🔧 Device Management & Monitoring',
    loginFeature4: '🔒 Secure & HIPAA Compliant',
    showLoginLeftPanel: true
  })

  useEffect(() => {
    const loadBranding = async () => {
      try {
        // Try to fetch from API first (public endpoint, no auth required)
        const response = await fetch('/api/v1/settings/branding/active/')

        if (response.ok) {
          const data = await response.json()
          setBranding({
            systemName: data.systemName || 'PIS',
            systemFullName: data.systemFullName || 'Pathology Information System',
            systemIcon: data.systemIcon || '🏥',
            logoUrl: data.logoUrl || '',
            loginPageTitle: data.loginPageTitle || 'Welcome to PIS',
            loginPageSubtitle: data.loginPageSubtitle || 'Advanced digital pathology platform',
            loginPageDescription: data.loginPageDescription || 'Advanced digital pathology platform for medical imaging, diagnosis management, and laboratory workflow optimization.',
            loginFeature1: data.loginFeature1 || '🔬 Digital Pathology Management',
            loginFeature2: data.loginFeature2 || '📊 Advanced Analytics & Reports',
            loginFeature3: data.loginFeature3 || '🔧 Device Management & Monitoring',
            loginFeature4: data.loginFeature4 || '🔒 Secure & HIPAA Compliant',
            showLoginLeftPanel: data.showLoginLeftPanel !== undefined ? data.showLoginLeftPanel : true
          })

          // Cache in localStorage
          localStorage.setItem('brandingSettings', JSON.stringify(data))

          // Update page title
          document.title = `${data.systemName || 'PIS'} - Login`
        } else {
          // Fallback to localStorage
          const savedSettings = localStorage.getItem('brandingSettings')
          if (savedSettings) {
            const parsed = JSON.parse(savedSettings)
            setBranding({
              systemName: parsed.systemName || 'PIS',
              systemFullName: parsed.systemFullName || 'Pathology Information System',
              systemIcon: parsed.systemIcon || '🏥',
              logoUrl: parsed.logoUrl || '',
              loginPageTitle: parsed.loginPageTitle || 'Welcome to PIS',
              loginPageSubtitle: parsed.loginPageSubtitle || 'Advanced digital pathology platform',
              loginPageDescription: parsed.loginPageDescription || 'Advanced digital pathology platform for medical imaging, diagnosis management, and laboratory workflow optimization.',
              loginFeature1: parsed.loginFeature1 || '🔬 Digital Pathology Management',
              loginFeature2: parsed.loginFeature2 || '📊 Advanced Analytics & Reports',
              loginFeature3: parsed.loginFeature3 || '🔧 Device Management & Monitoring',
              loginFeature4: parsed.loginFeature4 || '🔒 Secure & HIPAA Compliant',
              showLoginLeftPanel: parsed.showLoginLeftPanel !== undefined ? parsed.showLoginLeftPanel : true
            })
            document.title = `${parsed.systemName || 'PIS'} - Login`
          }
        }
      } catch (error) {
        console.error('Failed to load branding settings from API:', error)
        // Fallback to localStorage
        const savedSettings = localStorage.getItem('brandingSettings')
        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings)
            setBranding({
              systemName: parsed.systemName || 'PIS',
              systemFullName: parsed.systemFullName || 'Pathology Information System',
              systemIcon: parsed.systemIcon || '🏥',
              logoUrl: parsed.logoUrl || '',
              loginPageTitle: parsed.loginPageTitle || 'Welcome to PIS',
              loginPageSubtitle: parsed.loginPageSubtitle || 'Advanced digital pathology platform',
              loginPageDescription: parsed.loginPageDescription || 'Advanced digital pathology platform for medical imaging, diagnosis management, and laboratory workflow optimization.',
              loginFeature1: parsed.loginFeature1 || '🔬 Digital Pathology Management',
              loginFeature2: parsed.loginFeature2 || '📊 Advanced Analytics & Reports',
              loginFeature3: parsed.loginFeature3 || '🔧 Device Management & Monitoring',
              loginFeature4: parsed.loginFeature4 || '🔒 Secure & HIPAA Compliant',
              showLoginLeftPanel: parsed.showLoginLeftPanel !== undefined ? parsed.showLoginLeftPanel : true
            })
            document.title = `${parsed.systemName || 'PIS'} - Login`
          } catch (parseError) {
            console.error('Failed to parse localStorage settings:', parseError)
          }
        }
      }
    }

    loadBranding()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Real API authentication
      const response = await fetch('/api/v1/auth/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      })

      if (!response.ok) {
        throw new Error('Invalid credentials')
      }

      const data = await response.json()

      // Store auth token and user info
      localStorage.setItem('authToken', data.token)
      localStorage.setItem('user', JSON.stringify({
        id: data.user.id,
        username: data.user.username,
        email: data.user.email,
        name: `${data.user.first_name} ${data.user.last_name}`.trim() || data.user.username,
        role: data.user.role,
        department: data.user.department,
        organization: data.user.organization,
        organization_name: data.user.organization_name,
        is_staff: data.user.is_staff,
        is_superuser: data.user.is_superuser,
        avatar: '👨‍⚕️'
      }))

      toast.success('Login successful!')
      navigate('/dashboard')
    } catch (error) {
      toast.error('Invalid credentials')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      {branding.showLoginLeftPanel && (
        <div className="hidden lg:flex lg:flex-1 lg:flex-col lg:justify-center lg:px-12 xl:px-24 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-md">
          <div className="text-white mb-8">
            <h1 className="text-4xl font-bold mb-4 flex items-center space-x-3">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt="Logo"
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <span className="text-5xl">{branding.systemIcon}</span>
              )}
              <span>{branding.systemName}</span>
            </h1>
            <p className="text-xl text-blue-100 mb-6">
              {branding.systemFullName}
            </p>
            <p className="text-blue-200">
              {branding.loginPageDescription}
            </p>
          </div>

          <div className="space-y-4 text-blue-100">
            {branding.loginFeature1 && (
              <div className="flex items-start space-x-3">
                <span className="text-2xl flex-shrink-0">{branding.loginFeature1.match(/^[\u{1F300}-\u{1F9FF}]/u)?.[0] || '•'}</span>
                <span>{branding.loginFeature1.replace(/^[\u{1F300}-\u{1F9FF}]\s*/u, '')}</span>
              </div>
            )}
            {branding.loginFeature2 && (
              <div className="flex items-start space-x-3">
                <span className="text-2xl flex-shrink-0">{branding.loginFeature2.match(/^[\u{1F300}-\u{1F9FF}]/u)?.[0] || '•'}</span>
                <span>{branding.loginFeature2.replace(/^[\u{1F300}-\u{1F9FF}]\s*/u, '')}</span>
              </div>
            )}
            {branding.loginFeature3 && (
              <div className="flex items-start space-x-3">
                <span className="text-2xl flex-shrink-0">{branding.loginFeature3.match(/^[\u{1F300}-\u{1F9FF}]/u)?.[0] || '•'}</span>
                <span>{branding.loginFeature3.replace(/^[\u{1F300}-\u{1F9FF}]\s*/u, '')}</span>
              </div>
            )}
            {branding.loginFeature4 && (
              <div className="flex items-start space-x-3">
                <span className="text-2xl flex-shrink-0">{branding.loginFeature4.match(/^[\u{1F300}-\u{1F9FF}]/u)?.[0] || '•'}</span>
                <span>{branding.loginFeature4.replace(/^[\u{1F300}-\u{1F9FF}]\s*/u, '')}</span>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Right side - Login Form */}
      <div className={`flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-white ${branding.showLoginLeftPanel ? 'flex-1' : 'min-h-screen w-full'}`}>
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
              {branding.loginPageTitle}
            </h2>
            <p className="text-sm text-gray-600 mb-8">
              Please sign in to access the {branding.systemName} system
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm transition-colors"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm transition-colors"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                  Forgot password?
                </a>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center mb-2">
                <strong>Demo Credentials:</strong>
              </p>
              <p className="text-sm text-gray-700 text-center">
                Username: <code className="bg-white px-2 py-1 rounded">admin</code> |
                Password: <code className="bg-white px-2 py-1 rounded">admin123</code>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Login